import { ToggleButton as MuiToggleButton } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createStorePerformanceRequest, StorePerformanceInterval } from '../../../../../constants';
import { BopisLeadTimeData, useAsyncAction, useRfid } from '../../../../../hooks';
import { Icon, Icons } from '../../../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { ToggleButton } from '../../../../../stories/molecules/toggleButton/ToggleButton';
import { homeTranslations } from '../../../../../translations';
import { isFailureResponse } from '../../../../../utils';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import { GraphContent } from './GraphContent';
import { OverviewContent } from './OverviewContent';

export type BopisLeadTimeWidgetProps = {
  interval: StorePerformanceInterval;
  currentDate: Date;
};
export const BopisLeadTimeWidget: FC<BopisLeadTimeWidgetProps> = observer(({ interval, currentDate }) => {
  const { t } = useTranslation();
  const { bopisLeadTime: bopisLeadTimeTranslations, bopis: bopisTranslations } = homeTranslations;
  const [currentView, setCurrentView] = useState<'overview' | 'graph'>('overview');
  const { fetchBopisLeadTimeData } = useRfid();

  const {
    result: bopisLeadTimeData,
    isLoading: bopisLeadTimeLoading,
    isError: bopisLeadTimeError,
    triggerAction: triggerFetchBopisLeadTimeData,
  } = useAsyncAction<BopisLeadTimeData>();

  const fetchData = useCallback(async () => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });

    const response = await fetchBopisLeadTimeData(request);

    if (isFailureResponse(response)) {
      throw new Error('Failed to fetch data');
    }
    return response.data;
  }, [interval, currentDate]);

  const fetchWidgetData = () => {
    triggerFetchBopisLeadTimeData(fetchData);
  };

  const handleChangeView = (event: React.MouseEvent<HTMLElement>, newView: string) => {
    setCurrentView(newView as 'overview' | 'graph');
  };

  useEffect(() => {
    fetchWidgetData();
  }, [currentDate]);

  const turnaroundTimeMinutes = useMemo(() => {
    if (!bopisLeadTimeData) return 0;
    const { totalOrders, totalMinutesSpent } = bopisLeadTimeData;
    return totalOrders > 0 ? totalMinutesSpent / totalOrders : 0;
  }, [bopisLeadTimeData]);

  const customerPromise = useMemo(() => {
    if (!bopisLeadTimeData) return 0;

    const { totalOrders, compliantOrders } = bopisLeadTimeData;
    return totalOrders > 0 ? (compliantOrders / totalOrders) * 100 : 0;
  }, [bopisLeadTimeData]);

  const isNoData = useMemo(() => {
    return !bopisLeadTimeData || bopisLeadTimeData.totalOrders === 0;
  }, [bopisLeadTimeData]);

  return (
    <WidgetCardContent
      headerIcon={Icons.PROFILE}
      headerText={t(bopisLeadTimeTranslations.title.key, bopisLeadTimeTranslations.title.defaultValue)}
      subHeaderText={t(bopisLeadTimeTranslations.subTitle.key, bopisLeadTimeTranslations.subTitle.defaultValue)}
      toolTipText={t(bopisLeadTimeTranslations.toolTip.key, bopisLeadTimeTranslations.toolTip.defaultValue)}
      isLoading={bopisLeadTimeLoading}
      isError={bopisLeadTimeError}
      errorText={t(bopisLeadTimeTranslations.error.key, bopisLeadTimeTranslations.error.defaultValue)}
      isNoData={isNoData}
      noDataText={t(bopisTranslations.noDataText.key, bopisTranslations.noDataText.defaultValue)}
      lastUpdatedDate={bopisLeadTimeData?.lastUpdated}
      headerSideContent={
        <RowFlexContainer alignItems="center">
          <ToggleButton
            value={currentView}
            onChange={handleChangeView}
            leftButton={
              <MuiToggleButton value="overview" aria-label="overview button">
                <Icon data-testid="left-icon" icon={Icons.ALLWIDGETS} size="small" />
              </MuiToggleButton>
            }
            rightButton={
              <MuiToggleButton value="graph" aria-label="graph button">
                <Icon data-testid="right-icon" icon={Icons.TRENDUP} size="small" />
              </MuiToggleButton>
            }
          />
        </RowFlexContainer>
      }
      content={
        <ColumnFlexContainer flex={1} data-testid="bopis-lead-time-widget-wrapper" height="100%">
          {bopisLeadTimeData && currentView === 'overview' && (
            <OverviewContent
              turnaroundTimeMinutes={turnaroundTimeMinutes}
              customerPromise={customerPromise}
              pickMinutes={bopisLeadTimeData.pickMinutes}
              packMinutes={bopisLeadTimeData.packMinutes}
            />
          )}

          {bopisLeadTimeData && currentView === 'graph' && (
            <GraphContent customerPromise={customerPromise} interval={interval} items={bopisLeadTimeData.items} />
          )}
        </ColumnFlexContainer>
      }
    ></WidgetCardContent>
  );
});
